// src/lib/api.ts

import type {
  GeoMe,
  HijriConvertG2HResponse,
  HijriConvertH2GResponse,
  IslamicEvent,
  Location,
  PrayerDay,
  Settings
} from "./types";
import { getJson } from "./http";
import { getEffectiveSettings } from "./hijriRegional";

export function apiBase(): string {
  const explicitApiBase = (import.meta.env.VITE_API_BASE || "").trim();

  if (explicitApiBase) {
    if (/^https?:\/\//i.test(explicitApiBase)) {
      return explicitApiBase.replace(/\/+$/, "") + "/api";
    }

    return new URL(
      explicitApiBase.replace(/\/+$/, "") + "/api",
      window.location.origin
    ).toString();
  }

  const base = import.meta.env.BASE_URL || "/";
  return new URL(base.replace(/\/+$/, "") + "/api", window.location.origin).toString();
}

function apiUrl(path: string): URL {
  const base = apiBase();
  if (/^https?:\/\//i.test(base)) {
    return new URL(`${base}${path}`);
  }
  return new URL(`${base}${path}`, window.location.origin);
}

export async function geoSearch(q: string): Promise<{ results: Location[] }> {
  const u = apiUrl("/geo/search");
  u.searchParams.set("q", q);
  const res = await getJson<{ results: Location[] }>(u.toString());
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function geoReverse(lat: number, lon: number, timeZone?: string): Promise<Location> {
  const u = apiUrl("/geo/reverse");
  u.searchParams.set("lat", String(lat));
  u.searchParams.set("lon", String(lon));
  if (timeZone && timeZone.trim()) u.searchParams.set("tz", timeZone.trim());
  const res = await getJson<Location>(u.toString());
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function geoMe(): Promise<GeoMe> {
  const res = await getJson<GeoMe>(apiUrl("/geo/me").toString());
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function prayerDay(
  loc: Pick<Location, "city" | "country" | "timezone">,
  dateISO: string,
  s: Settings
): Promise<{ day: PrayerDay }> {
  const effectiveSettings = getEffectiveSettings(s, loc);
  const u = apiUrl("/prayer/day");
  u.searchParams.set("city", loc.city);
  u.searchParams.set("country", loc.country);
  u.searchParams.set("date", dateISO);
  u.searchParams.set("method", String(effectiveSettings.methodId));
  u.searchParams.set("madhhab", effectiveSettings.madhhab);
  u.searchParams.set("hijriAdjustment", String(effectiveSettings.hijriAdjustment));
  if (loc.timezone) u.searchParams.set("tz", loc.timezone);

  const res = await getJson<{ day: PrayerDay }>(u.toString());
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function prayerMonth(
  loc: Pick<Location, "city" | "country" | "timezone">,
  month: number,
  year: number,
  s: Settings
): Promise<{ days: PrayerDay[] }> {
  const effectiveSettings = getEffectiveSettings(s, loc);
  const u = apiUrl("/prayer/month");
  u.searchParams.set("city", loc.city);
  u.searchParams.set("country", loc.country);
  u.searchParams.set("month", String(month));
  u.searchParams.set("year", String(year));
  u.searchParams.set("method", String(effectiveSettings.methodId));
  u.searchParams.set("madhhab", effectiveSettings.madhhab);
  u.searchParams.set("hijriAdjustment", String(effectiveSettings.hijriAdjustment));
  if (loc.timezone) u.searchParams.set("tz", loc.timezone);

  const res = await getJson<{ days: PrayerDay[] }>(u.toString(), 15000);
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function hijriConvertG2H(dateISO: string, hijriAdjustment: number): Promise<HijriConvertG2HResponse> {
  const u = apiUrl("/hijri/convert");
  u.searchParams.set("mode", "g2h");
  u.searchParams.set("date", dateISO);
  u.searchParams.set("hijriAdjustment", String(hijriAdjustment));
  u.searchParams.set("_ts", String(Date.now()));
  const res = await getJson<HijriConvertG2HResponse>(u.toString());
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function hijriConvertH2G(hDay: number, hMonth: number, hYear: number): Promise<HijriConvertH2GResponse> {
  const u = apiUrl("/hijri/convert");
  u.searchParams.set("mode", "h2g");
  u.searchParams.set("hDay", String(hDay));
  u.searchParams.set("hMonth", String(hMonth));
  u.searchParams.set("hYear", String(hYear));
  u.searchParams.set("_ts", String(Date.now()));
  const res = await getJson<HijriConvertH2GResponse>(u.toString());
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function eventsUpcoming(
  fromISO: string,
  location: Pick<Location, "city" | "country" | "timezone"> | null | undefined,
  s: Settings,
  limit = 10
): Promise<{ events: IslamicEvent[]; disclaimer: string }> {
  const effectiveSettings = getEffectiveSettings(s, location);
  if (!location?.city || !location.country) {
    throw new Error("Select a city before loading Islamic events.");
  }

  const u = apiUrl("/events");
  u.searchParams.set("from", fromISO);
  u.searchParams.set("limit", String(limit));
  u.searchParams.set("hijriAdjustment", String(effectiveSettings.hijriAdjustment));
  u.searchParams.set("method", String(effectiveSettings.methodId));
  u.searchParams.set("city", location.city);
  u.searchParams.set("country", location.country);
  if (location.timezone) u.searchParams.set("tz", location.timezone);

  const res = await getJson<{ events: IslamicEvent[]; disclaimer: string }>(u.toString(), 20000);
  if (!res.ok) throw new Error(res.error);
  return res.data;
}
